import React, { type ComponentProps, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { OriginalAsset } from '@bynder/image-gen-frontend-lib';
import { notify } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { getUser } from 'packages/store/user/user.selectors';
import ImageGen from './ImageGenModal';
import assetsService from 'packages/services/assetsService';
import permissionConfig from 'packages/configs/permissions';
import { ImageGenInput, isAllowedImageFormat } from './utils';

const useImageGenModal = (props: {
    handleAsset?: (asset: any, width: number, height: number) => void;
    shouldCloseAfterSave?: boolean;
    saveButtonLabel?: string;
}) => {
    const sessionId = useRef('');
    const { translate } = useTranslate();

    const userPermissions = useSelector(getUser)?.permissions || [];
    const hasEditWithAiPermission = userPermissions.includes(permissionConfig.ASSETS.AI_EDIT);

    const [isOpen, setOpen] = useState(false);
    const [originalAsset, setOriginalAsset] = useState<OriginalAsset>(null);

    const isAllowedToUse = (input: ImageGenInput) => {
        if (!hasEditWithAiPermission) {
            return false;
        }

        return isAllowedImageFormat(input);
    };

    const onOpen = (prop: Pick<ComponentProps<typeof ImageGen>, 'originalAsset'>) => {
        sessionId.current = uuidv4();
        setOriginalAsset(prop.originalAsset);
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    const onSave = async (data: {
        fileId: string;
        name?: string;
        copyMetaproperties?: boolean;
        width?: number;
        height?: number;
    }) => {
        try {
            const { status, asset } = await assetsService.saveAIEdit({
                fileId: data.fileId,
                name: data.name || `${originalAsset.name}-AI-edited`,
                copyMetaproperties: data.copyMetaproperties,
            });

            if (status === 200) {
                if (props.handleAsset) {
                    props.handleAsset(asset, data.width, data.height);
                }
            } else {
                notify({
                    title: translate('image-gen.modal.save.notification.failed'),
                    variant: 'error',
                });
            }
        } catch (err) {
            notify({
                title: translate('image-gen.modal.save.notification.error'),
                variant: 'error',
            });
            console.error('Error saving AI edited asset:', err);
        } finally {
            if (props.shouldCloseAfterSave) {
                onClose();
            }
        }
    };

    const renderImageGenModal = () => {
        if (!hasEditWithAiPermission || !originalAsset) {
            return null;
        }

        return (
            <ImageGen
                originalAsset={originalAsset}
                saveButtonLabel={props.saveButtonLabel}
                sessionId={sessionId.current}
                isOpen={isOpen}
                onClose={onClose}
                onSave={onSave}
            />
        );
    };

    return {
        setImageGenModal: onOpen,
        renderImageGenModal,
        isAllowedToUse,
    };
};

export default useImageGenModal;
